import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Link, useSearchParams } from 'react-router-dom';

import { getNews } from '../../utils/api';
import DOMPurify from 'dompurify';
import Tests from '../shorts/Tests';
import Article from '../news/Article';
import { Helmet } from 'react-helmet-async';
import AdSlot from '../ads/AdSlot';

import '../../assets/css/news.css';

const PAGE_SIZE_DEFAULT = 10; // Einträge pro Seite (anpassbar)

// --- Helpers: HTML -> Plaintext und sicheres Kürzen ---
const toPlainText = (html) =>
    DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    const t = String(text);
    if (t.length <= maxLength) return t;
    const cut = t.slice(0, maxLength);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + '…';
};

const ListView = ({ news }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // --- NEU: Tag aus URL ---
    const activeTag = (searchParams.get('tag') || '').trim();

    const initialPage = Number(searchParams.get('page')) > 0 ? Number(searchParams.get('page')) : 1;
    const initialPageSizeParam = Number(searchParams.get('pageSize'));
    const initialPageSize = [10, 20, 50].includes(initialPageSizeParam)
        ? initialPageSizeParam
        : PAGE_SIZE_DEFAULT;

    const [currentPage, setCurrentPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize);

    // Reagiere auf neue Daten oder Tag-Wechsel: Seite aus URL übernehmen (ansonsten 1)
    useEffect(() => {
        const fromUrl = Number(searchParams.get('page'));
        const psFromUrl = Number(searchParams.get('pageSize'));
        // Bei Tag-Wechsel immer auf 1
        setCurrentPage(activeTag ? 1 : (fromUrl > 0 ? fromUrl : 1));
        if ([10, 20, 50].includes(psFromUrl)) setPageSize(psFromUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [news.length, activeTag]);

    // Bei Seiten- oder PageSize-Änderung: URL aktualisieren + nach oben scrollen
    useEffect(() => {
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            // --- NEU: tag stabil in URL halten/entfernen ---
            if (activeTag) p.set('tag', activeTag); else p.delete('tag');

            if (currentPage > 1) p.set('page', String(currentPage));
            else p.delete('page');

            if ([10, 20, 50].includes(pageSize)) p.set('pageSize', String(pageSize));
            else p.delete('pageSize');

            return p;
        }, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage, pageSize, activeTag, setSearchParams]);

    // --- NEU: Flexible Tag-Prüfung (Array, CSV-String, Einzelstring) ---
    const hasTag = (item, tag) => {
        if (!tag) return true;
        const t = tag.toLowerCase();
        const raw =
            item?.tags ??
            item?.tag ??
            item?.keywords ??
            item?.keyword ??
            '';
        if (Array.isArray(raw)) {
            return raw.some((x) => String(x).toLowerCase().trim() === t);
        }
        // CSV oder Einzelstring
        return String(raw)
            .split(',')
            .map((x) => x.toLowerCase().trim())
            .filter(Boolean)
            .includes(t);
    };

    // --- NEU: gefilterte Liste nach Tag ---
    const filteredNews = useMemo(
        () => news.filter((n) => hasTag(n, activeTag)),
        [news, activeTag]
    );

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(filteredNews.length / pageSize)),
        [filteredNews.length, pageSize]
    );

    const paginatedNews = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredNews.slice(start, start + pageSize);
    }, [filteredNews, currentPage, pageSize]);

    const goToPage = (page) => {
        const p = Math.min(totalPages, Math.max(1, page));
        setCurrentPage(p);
    };

    const handlePageSizeChange = (e) => {
        const next = Number(e.target.value);
        if (![10, 20, 50].includes(next)) return;
        setCurrentPage(1); // bei Änderung zurück auf Seite 1
        setPageSize(next);
    };

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} um ${hours}:${minutes} Uhr`;
    };

    const getPageItems = (total, current) => {
        const items = [];
        if (total <= 7) {
            for (let i = 1; i <= total; i++) items.push(i);
            return items;
        }
        const add = (v) => items.push(v);
        add(1);
        const left = Math.max(2, current - 1);
        const right = Math.min(total - 1, current + 1);
        if (left > 2) add('…');
        for (let i = left; i <= right; i++) add(i);
        if (right < total - 1) add('…');
        add(total);
        return items;
    };

    // --- NEU: SEO dynamisch bei aktivem Tag ---
    const pageTitle = activeTag
        ? `News – Tag: ${activeTag} – Seite ${currentPage} | NGCTV`
        : `News – Seite ${currentPage} | NGCTV`;

    const pageDescription = activeTag
        ? `Artikel zum Tag „${activeTag}“ – Seite ${currentPage}. Stöbere durch gefilterte NGCTV-News.`
        : `Aktuelle News – Seite ${currentPage}. Stöbere durch die neuesten Artikel von NGCTV.`;

    // Canonical ohne Hash + mit stabilen Query-Parametern (inkl. tag)
    const canonicalQueryObj = {
        ...(activeTag ? { tag: activeTag } : {}),
        ...(currentPage > 1 ? { page: String(currentPage) } : {}),
        ...(pageSize !== PAGE_SIZE_DEFAULT ? { pageSize: String(pageSize) } : {}),
    };
    const canonicalQuery =
        Object.keys(canonicalQueryObj).length > 0
            ? `?${new URLSearchParams(canonicalQueryObj).toString()}`
            : '';

    return (
        <>
            {/* SEO / Head */}
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <link rel="canonical" href={`https://ngctv.de/news${canonicalQuery}`} />
            </Helmet>

            <main>
                <div className='wrapper'>
                    <div className='rows'>
                        <div className='row'>
                            <div className='category'>
                                News
                            </div>

                            <div className='tag-pill-box'>
                                {activeTag ? <> <span className="tag-pill">Suche nach Tag: #{activeTag}</span></> : null}
                            </div>

                            <div className='news-controls'>
                                <label htmlFor='pageSize' className='page-size-label'>Artikel pro Seite:&nbsp;</label>
                                <select
                                    id='pageSize'
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                    className='page-size-select'
                                    aria-label='Artikel pro Seite wählen'
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>

                            <div className='news'>
                                {paginatedNews.length === 0 ? (
                                    <div className='news-empty'>
                                        {activeTag
                                            ? <>Keine Einträge für den Tag „{activeTag}“ vorhanden.</>
                                            : <>Keine Einträge vorhanden.</>}
                                    </div>
                                ) : (
                                    paginatedNews.map((newsItem) => {
                                        const preview = truncateText(toPlainText(newsItem.content), 180);
                                        return (
                                            <Link
                                                to={`/news/${newsItem.site_name}`}
                                                viewTransition="true"
                                                key={newsItem.id}
                                            >
                                                <div className='news-entry'>
                                                    <img
                                                        src={`/news/bilder/${newsItem.image}`}
                                                        alt={newsItem.headline}
                                                    />
                                                    <div>
                                                        <div className='news-date'>{formatDate(newsItem.date)}</div>
                                                        <div className='news-headline'>{newsItem.headline}</div>
                                                        <p>{preview}</p>
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })
                                )}
                            </div>

                            {totalPages > 1 && (
                                <nav className='pagination' aria-label='News Pagination'>
                                    <button
                                        type='button'
                                        className='page-btn prev'
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        aria-label='Vorherige Seite'
                                    >
                                        « Zurück
                                    </button>

                                    <ul className='page-list' role='list'>
                                        {getPageItems(totalPages, currentPage).map((item, idx) => (
                                            <li key={idx}>
                                                {item === '…' ? (
                                                    <span className='page-ellipsis' aria-hidden>
                                                        …
                                                    </span>
                                                ) : (
                                                    <button
                                                        type='button'
                                                        className={`page-btn number ${item === currentPage ? 'active' : ''}`}
                                                        onClick={() => goToPage(item)}
                                                        aria-current={item === currentPage ? 'page' : undefined}
                                                        aria-label={`Seite ${item}`}
                                                    >
                                                        {item}
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        type='button'
                                        className='page-btn next'
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        aria-label='Nächste Seite'
                                    >
                                        Weiter »
                                    </button>
                                </nav>
                            )}
                        </div>

                        <div className='row'>
                            <Tests />
                            <AdSlot slot="2119524913" />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

const News = () => {
    const [news, setNews] = useState([]);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const data = await getNews();
                setNews(data?.news || []);
            } catch (err) {
                console.error(err);
            }
        };

        fetchNews();
    }, []);

    return (
        <Routes>
            {/* FIX: Index-Route statt path='/' */}
            <Route index element={<ListView news={news} />} />
            {/* Relativer Detail-Pfad */}
            <Route path=":siteName" element={<Article />} />
        </Routes>
    );
};

export default News;
